import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../context/AuthContext';

const UserPayments = () => {
  const { user } = useContext(AuthContext);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailTransaction, setDetailTransaction] = useState(null);
  const [detailRoutes, setDetailRoutes] = useState([]);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [sharedPayment, setSharedPayment] = useState(null);
  const [loadingSharedPayment, setLoadingSharedPayment] = useState(true);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [facturaFile, setFacturaFile] = useState(null);
  const [uploadingFactura, setUploadingFactura] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  useEffect(() => {
    fetchTransactions();
    fetchSharedPayment();
  }, []);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/user/transactions');
      setTransactions(res.data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      if (error.response?.status !== 404) {
        alert('Error al cargar transacciones: ' + (error.response?.data?.message || error.message));
      }
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSharedPayment = async () => {
    try {
      setLoadingSharedPayment(true);
      const res = await axios.get('/api/user/shared-payment');
      setSharedPayment(res.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setSharedPayment(null);
      } else {
        console.error('Error fetching shared payment:', error);
      }
    } finally {
      setLoadingSharedPayment(false);
    }
  };
  
  const handleApprovePayment = async (e) => {
    e.preventDefault();
    if (!facturaFile) {
      alert('Por favor, selecciona un archivo de factura');
      return;
    }
    
    setUploadingFactura(true);
    try {
      const formData = new FormData();
      formData.append('factura', facturaFile);
      
      await axios.post('/api/user/shared-payment/approve', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      alert('Pago aprobado y factura subida exitosamente');
      setShowApprovalModal(false);
      setFacturaFile(null);
      fetchSharedPayment();
      fetchTransactions();
    } catch (error) {
      alert('Error al aprobar pago: ' + (error.response?.data?.message || error.message));
    } finally {
      setUploadingFactura(false);
    }
  };

  const handleRejectPayment = async (e) => {
    e.preventDefault();
    if (!rejectionReason.trim()) {
      alert('Por favor, proporciona una razón para el rechazo');
      return;
    }
    
    setRejecting(true);
    try {
      await axios.post('/api/user/shared-payment/reject', {
        rejectionReason: rejectionReason.trim()
      });
      
      alert('Pago rechazado exitosamente');
      setShowRejectModal(false);
      setRejectionReason('');
      fetchSharedPayment();
    } catch (error) {
      alert('Error al rechazar pago: ' + (error.response?.data?.message || error.message));
    } finally {
      setRejecting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('es-CL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleStateClick = async (transaction) => {
    if (transaction && transaction.paidAt) {
      setDetailTransaction(transaction);
      setShowDetailModal(true);
      setLoadingRoutes(true);
      setDetailRoutes([]);

      // Fetch route details for all routeIds in the transaction
      if (transaction.routeIds && transaction.routeIds.length > 0) {
        try {
          const routePromises = transaction.routeIds.map(routeId => {
            const routeIdStr = typeof routeId === 'string' ? routeId : routeId.toString();
            return axios.get(`/api/routes/${routeIdStr}`);
          });
          const routeResponses = await Promise.all(routePromises);
          const routes = routeResponses.map(res => res.data);
          setDetailRoutes(routes);
        } catch (error) {
          console.error('Error fetching route details:', error);
          alert('Error al cargar detalles de rutas: ' + (error.response?.data?.message || error.message));
        } finally {
          setLoadingRoutes(false);
        }
      } else {
        setLoadingRoutes(false);
      }
    }
  };

  const handlePhotoClick = (transaction) => {
    setSelectedTransaction(transaction);
    setShowPhotoModal(true);
  };

  const handleClosePhotoModal = () => {
    setShowPhotoModal(false);
    setSelectedTransaction(null);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setDetailTransaction(null);
    setDetailRoutes([]);
  };

  if (loading) {
    return <div className="loading">Cargando información de transacciones...</div>;
  }

  return (
    <div className="container">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '30px', 
        flexWrap: 'wrap', 
        gap: '15px',
        paddingBottom: '15px',
        borderBottom: '2px solid #e5e7eb'
      }}>
        <div>
          <h2 style={{ margin: 0, color: '#1e40af', fontSize: '28px', fontWeight: '600' }}>
            Transacciones
          </h2>
          <p style={{ margin: '5px 0 0 0', color: '#6b7280', fontSize: '14px' }}>
            Historial completo de pagos recibidos
          </p>
        </div>
        <button 
          className="btn btn-secondary" 
          onClick={() => {
            fetchTransactions();
            fetchSharedPayment();
          }} 
          style={{ 
            minWidth: '120px',
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: '500',
            borderRadius: '6px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.transform = 'translateY(-1px)'}
          onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
        >
          🔄 Actualizar
        </button>
      </div>

      {/* Shared Payment Section */}
      {!loadingSharedPayment && sharedPayment && (
        <div className="card" style={{
          marginBottom: '30px',
          backgroundColor: '#fef3c7',
          border: '2px solid #fbbf24',
          borderRadius: '12px',
          padding: '24px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
            <div>
              <h3 style={{ margin: 0, color: '#92400e', fontSize: '20px', fontWeight: '600', marginBottom: '8px' }}>
                ⚠️ Pago Compartido - Pendiente de Aprobación
              </h3>
              <p style={{ margin: 0, color: '#78350f', fontSize: '14px' }}>
                El administrador ha compartido un monto de pago que requiere tu aprobación
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                className="btn btn-success"
                onClick={() => setShowApprovalModal(true)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  border: 'none',
                  backgroundColor: '#10b981',
                  color: 'white'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#059669';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#10b981';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                ✓ Aprobar y Subir Factura
              </button>
              <button
                className="btn btn-danger"
                onClick={() => setShowRejectModal(true)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  border: 'none',
                  backgroundColor: '#ef4444',
                  color: 'white'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#dc2626';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#ef4444';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                ✕ Rechazar Pago
              </button>
            </div>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginTop: '20px'
          }}>
            <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Monto</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#1e40af' }}>
                {formatCurrency(sharedPayment.amount || 0)}
              </div>
            </div>
            <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Descuento</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#1e40af' }}>
                {formatCurrency(sharedPayment.discount || 0)}
              </div>
            </div>
            <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Total</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#1e40af' }}>
                {formatCurrency(sharedPayment.total || 0)}
              </div>
            </div>
            <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>IVA</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#1e40af' }}>
                {sharedPayment.vat ? sharedPayment.vat.toFixed(2) : '0.00'}
              </div>
            </div>
            <div style={{ backgroundColor: '#10b981', padding: '16px', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: 'white', marginBottom: '4px', opacity: 0.9 }}>A Pagar</div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: 'white' }}>
                {formatCurrency(sharedPayment.toPay || 0)}
              </div>
            </div>
          </div>
          {sharedPayment.sharedAt && (
            <div style={{ marginTop: '16px', fontSize: '12px', color: '#78350f' }}>
              Compartido el: {formatDate(sharedPayment.sharedAt)}
            </div>
          )}
        </div>
      )}

      {transactions.length === 0 ? (
        <div className="card" style={{
          textAlign: 'center',
          padding: '60px 20px',
          backgroundColor: '#f9fafb',
          border: '2px dashed #d1d5db',
          borderRadius: '12px'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>📋</div>
          <h3 style={{ color: '#374151', marginBottom: '10px' }}>No hay transacciones registradas</h3>
          <p style={{ color: '#6b7280', margin: 0 }}>
            Las transacciones aparecerán aquí una vez que se realicen los pagos.
          </p>
        </div>
      ) : (
        <div className="card" style={{
          borderRadius: '12px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          overflow: 'hidden'
        }}>
          <div className="table-responsive">
            <table className="data-table" style={{
              width: '100%',
              borderCollapse: 'separate',
              borderSpacing: 0
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f3f4f6' }}>
                  <th style={{ 
                    padding: '16px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    color: '#374151',
                    borderBottom: '2px solid #e5e7eb',
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Fecha de Pago</th>
                  <th style={{ 
                    padding: '16px', 
                    textAlign: 'center', 
                    fontWeight: '600',
                    color: '#374151',
                    borderBottom: '2px solid #e5e7eb',
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Aprobados</th>
                  <th style={{ 
                    padding: '16px', 
                    textAlign: 'center', 
                    fontWeight: '600',
                    color: '#374151',
                    borderBottom: '2px solid #e5e7eb',
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Rechazados</th>
                  <th style={{ 
                    padding: '16px', 
                    textAlign: 'center', 
                    fontWeight: '600',
                    color: '#374151',
                    borderBottom: '2px solid #e5e7eb',
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Viajes</th>
                  <th style={{ 
                    padding: '16px', 
                    textAlign: 'right', 
                    fontWeight: '600',
                    color: '#374151',
                    borderBottom: '2px solid #e5e7eb',
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Monto</th>
                  <th style={{ 
                    padding: '16px', 
                    textAlign: 'right', 
                    fontWeight: '600',
                    color: '#374151',
                    borderBottom: '2px solid #e5e7eb',
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Descuento</th>
                  <th style={{ 
                    padding: '16px', 
                    textAlign: 'right', 
                    fontWeight: '600',
                    color: '#374151',
                    borderBottom: '2px solid #e5e7eb',
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Total</th>
                  <th style={{ 
                    padding: '16px', 
                    textAlign: 'center', 
                    fontWeight: '600',
                    color: '#374151',
                    borderBottom: '2px solid #e5e7eb',
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>IVA</th>
                  <th style={{ 
                    padding: '16px', 
                    textAlign: 'right', 
                    fontWeight: '600',
                    color: '#374151',
                    borderBottom: '2px solid #e5e7eb',
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>A Pagar</th>
                  <th style={{ 
                    padding: '16px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    color: '#374151',
                    borderBottom: '2px solid #e5e7eb',
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Pagado Por</th>
                  <th style={{ 
                    padding: '16px', 
                    textAlign: 'center', 
                    fontWeight: '600',
                    color: '#374151',
                    borderBottom: '2px solid #e5e7eb',
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Estado</th>
                  <th style={{ 
                    padding: '16px', 
                    textAlign: 'center', 
                    fontWeight: '600',
                    color: '#374151',
                    borderBottom: '2px solid #e5e7eb',
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction, index) => (
                  <tr 
                    key={transaction._id}
                    style={{
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                      transition: 'background-color 0.2s',
                      borderBottom: '1px solid #e5e7eb'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9fafb'}
                  >
                    <td data-label="Fecha de Pago" style={{ padding: '16px', color: '#374151' }}>
                      <div style={{ fontSize: '13px', fontWeight: '500' }}>
                        {formatDate(transaction.paidAt)}
                      </div>
                    </td>
                    <td data-label="Número de Aprobados" style={{ padding: '16px', textAlign: 'center' }}>
                      <span style={{ 
                        color: '#10b981', 
                        fontWeight: 'bold',
                        fontSize: '15px',
                        backgroundColor: '#d1fae5',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        display: 'inline-block'
                      }}>
                        {transaction.numberOfApproved || 0}
                      </span>
                    </td>
                    <td data-label="Número de Rechazados" style={{ padding: '16px', textAlign: 'center' }}>
                      <span style={{ 
                        color: '#ef4444', 
                        fontWeight: 'bold',
                        fontSize: '15px',
                        backgroundColor: '#fee2e2',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        display: 'inline-block'
                      }}>
                        {transaction.numberOfDeclined || 0}
                      </span>
                    </td>
                    <td data-label="Número de Viajes" style={{ padding: '16px', textAlign: 'center' }}>
                      <strong style={{ color: '#1e40af', fontSize: '15px' }}>
                        {transaction.numberOfTrips || 0}
                      </strong>
                    </td>
                    <td data-label="Monto" style={{ padding: '16px', textAlign: 'right', color: '#374151' }}>
                      {formatCurrency(transaction.amount || 0)}
                    </td>
                    <td data-label="Descuento" style={{ padding: '16px', textAlign: 'right', color: '#6b7280' }}>
                      {formatCurrency(transaction.discount || 0)}
                    </td>
                    <td data-label="Total" style={{ padding: '16px', textAlign: 'right' }}>
                      <strong style={{ color: '#374151', fontSize: '15px' }}>
                        {formatCurrency(transaction.total || 0)}
                      </strong>
                    </td>
                    <td data-label="IVA" style={{ padding: '16px', textAlign: 'center', color: '#6b7280' }}>
                      {transaction.vat ? transaction.vat.toFixed(2) : '0.00'}
                    </td>
                    <td data-label="A Pagar" style={{ padding: '16px', textAlign: 'right' }}>
                      <strong style={{ 
                        color: '#10b981', 
                        fontSize: '16px',
                        fontWeight: '600'
                      }}>
                        {formatCurrency(transaction.toPay || 0)}
                      </strong>
                    </td>
                    <td data-label="Pagado Por" style={{ padding: '16px', color: '#6b7280', fontSize: '14px' }}>
                      {transaction.paidBy?.name || 'N/A'}
                    </td>
                    <td data-label="Estado" style={{ padding: '16px', textAlign: 'center' }}>
                      <span
                        className="badge badge-completed"
                        style={{
                          cursor: 'pointer',
                          textTransform: 'none',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '600',
                          transition: 'all 0.2s',
                          display: 'inline-block'
                        }}
                        onClick={() => handleStateClick(transaction)}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'scale(1.05)';
                          e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'scale(1)';
                          e.target.style.boxShadow = 'none';
                        }}
                        title="Click para ver detalles de la transacción y rutas pagadas"
                      >
                        ✓ Éxito
                      </span>
                    </td>
                    <td data-label="Acciones" style={{ padding: '16px', textAlign: 'center' }}>
                      {transaction.paymentPhoto && transaction.paymentPhoto.path ? (
                        <button
                          className="btn btn-primary"
                          onClick={() => handlePhotoClick(transaction)}
                          style={{ 
                            padding: '6px 14px', 
                            fontSize: '12px',
                            borderRadius: '6px',
                            fontWeight: '500',
                            transition: 'all 0.2s',
                            border: 'none',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = 'none';
                          }}
                        >
                          📷 Ver Foto
                        </button>
                      ) : (
                        <span style={{ color: '#9ca3af', fontSize: '13px' }}>-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Payment Photo Modal */}
      {showPhotoModal && selectedTransaction && selectedTransaction.paymentPhoto && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '30px',
            maxWidth: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            position: 'relative',
            border: '1px solid #e5e7eb'
          }}>
            <button
              onClick={handleClosePhotoModal}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                cursor: 'pointer',
                fontSize: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#dc2626';
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#ef4444';
                e.target.style.transform = 'scale(1)';
              }}
            >
              ×
            </button>
            <h3 style={{ 
              marginBottom: '24px', 
              color: '#1e40af',
              fontSize: '24px',
              fontWeight: '600',
              paddingRight: '40px'
            }}>
              📷 Foto del Pago
            </h3>
            {selectedTransaction.paymentPhoto.path && (
              <img
                src={selectedTransaction.paymentPhoto.path}
                alt="Foto del pago"
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                }}
                onError={(e) => {
                  console.error('Error loading payment photo:', selectedTransaction.paymentPhoto.path);
                  const errorMsg = document.createElement('p');
                  errorMsg.style.color = '#ef4444';
                  errorMsg.textContent = 'Error al cargar la imagen. Verifique que el servidor esté ejecutándose.';
                  e.target.parentElement.appendChild(errorMsg);
                  e.target.style.display = 'none';
                }}
              />
            )}
            {selectedTransaction.paidAt && (
              <p style={{ marginTop: '15px', color: '#666', fontSize: '14px' }}>
                Pagado el: {formatDate(selectedTransaction.paidAt)}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && detailTransaction && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '40px',
            maxWidth: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            position: 'relative',
            width: '100%',
            maxWidth: '1000px',
            border: '1px solid #e5e7eb'
          }}>
            <button
              onClick={handleCloseDetailModal}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                cursor: 'pointer',
                fontSize: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#dc2626';
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#ef4444';
                e.target.style.transform = 'scale(1)';
              }}
            >
              ×
            </button>
            
            <h2 style={{ 
              marginBottom: '30px', 
              color: '#1e40af',
              fontSize: '28px',
              fontWeight: '600',
              paddingRight: '40px'
            }}>
              📋 Detalles de la Transacción
            </h2>

            {/* Transaction Summary */}
            <div style={{ 
              backgroundColor: '#ffffff', 
              padding: '28px', 
              borderRadius: '16px', 
              marginBottom: '32px',
              border: '2px solid #e5e7eb',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)'
            }}>
              <h4 style={{ 
                marginBottom: '20px', 
                color: '#1e40af',
                fontSize: '18px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '20px' }}>📊</span> Resumen de la Transacción
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '24px' }}>
                <div style={{
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '10px',
                  border: '1px solid #e5e7eb',
                  transition: 'all 0.2s'
                }}>
                  <strong style={{ color: '#6b7280', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '8px' }}>Empresa</strong>
                  <p style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                    {detailTransaction.companyName || user?.companyName || user?.name || 'N/A'}
                  </p>
                </div>
                <div style={{
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '10px',
                  border: '1px solid #e5e7eb',
                  transition: 'all 0.2s'
                }}>
                  <strong style={{ color: '#6b7280', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '8px' }}>Fecha de Pago</strong>
                  <p style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                    {formatDate(detailTransaction.paidAt)}
                  </p>
                </div>
                <div style={{
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '10px',
                  border: '1px solid #e5e7eb',
                  transition: 'all 0.2s'
                }}>
                  <strong style={{ color: '#6b7280', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '8px' }}>Pagado Por</strong>
                  <p style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                    {detailTransaction.paidBy?.name || 'N/A'}
                  </p>
                </div>
                <div style={{
                  padding: '16px',
                  backgroundColor: '#eff6ff',
                  borderRadius: '10px',
                  border: '2px solid #3b82f6',
                  transition: 'all 0.2s'
                }}>
                  <strong style={{ color: '#3b82f6', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '8px' }}>Número de Viajes</strong>
                  <p style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#1e40af' }}>
                    {detailTransaction.numberOfTrips || 0}
                  </p>
                </div>
                <div style={{
                  padding: '16px',
                  backgroundColor: '#d1fae5',
                  borderRadius: '10px',
                  border: '2px solid #10b981',
                  transition: 'all 0.2s'
                }}>
                  <strong style={{ color: '#059669', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '8px' }}>Aprobados</strong>
                  <p style={{ margin: 0, fontSize: '20px', color: '#10b981', fontWeight: '700' }}>
                    {detailTransaction.numberOfApproved || 0}
                  </p>
                </div>
                <div style={{
                  padding: '16px',
                  backgroundColor: '#fee2e2',
                  borderRadius: '10px',
                  border: '2px solid #ef4444',
                  transition: 'all 0.2s'
                }}>
                  <strong style={{ color: '#dc2626', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '8px' }}>Rechazados</strong>
                  <p style={{ margin: 0, fontSize: '20px', color: '#ef4444', fontWeight: '700' }}>
                    {detailTransaction.numberOfDeclined || 0}
                  </p>
                </div>
                <div style={{
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '10px',
                  border: '1px solid #e5e7eb',
                  transition: 'all 0.2s'
                }}>
                  <strong style={{ color: '#6b7280', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '8px' }}>Monto</strong>
                  <p style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                    {formatCurrency(detailTransaction.amount || 0)}
                  </p>
                </div>
                <div style={{
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '10px',
                  border: '1px solid #e5e7eb',
                  transition: 'all 0.2s'
                }}>
                  <strong style={{ color: '#6b7280', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '8px' }}>Descuento</strong>
                  <p style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#6b7280' }}>
                    {formatCurrency(detailTransaction.discount || 0)}
                  </p>
                </div>
                <div style={{
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '10px',
                  border: '1px solid #e5e7eb',
                  transition: 'all 0.2s'
                }}>
                  <strong style={{ color: '#6b7280', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '8px' }}>Total</strong>
                  <p style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1f2937' }}>
                    {formatCurrency(detailTransaction.total || 0)}
                  </p>
                </div>
                <div style={{
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '10px',
                  border: '1px solid #e5e7eb',
                  transition: 'all 0.2s'
                }}>
                  <strong style={{ color: '#6b7280', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '8px' }}>IVA</strong>
                  <p style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                    {detailTransaction.vat ? detailTransaction.vat.toFixed(2) : '0.00'}
                  </p>
                </div>
                <div style={{
                  padding: '16px',
                  backgroundColor: '#d1fae5',
                  borderRadius: '10px',
                  border: '2px solid #10b981',
                  transition: 'all 0.2s'
                }}>
                  <strong style={{ color: '#059669', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '8px' }}>A Pagar</strong>
                  <p style={{ margin: 0, fontSize: '22px', color: '#10b981', fontWeight: '700' }}>
                    {formatCurrency(detailTransaction.toPay || 0)}
                  </p>
                </div>
              </div>
            </div>

            {/* Routes List */}
            <div>
              <h3 style={{ 
                marginBottom: '20px', 
                color: '#1e40af',
                fontSize: '22px',
                fontWeight: '600',
                paddingBottom: '16px',
                borderBottom: '3px solid #3b82f6',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <span style={{ fontSize: '24px' }}>🚛</span> Rutas Incluidas 
                <span style={{ 
                  backgroundColor: '#3b82f6', 
                  color: 'white', 
                  padding: '4px 12px', 
                  borderRadius: '12px', 
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  {detailTransaction.routeIds?.length || 0}
                </span>
              </h3>
              
              {loadingRoutes ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <p>Cargando detalles de rutas...</p>
                </div>
              ) : detailRoutes.length === 0 ? (
                <div style={{ 
                  backgroundColor: '#fef3c7', 
                  padding: '15px', 
                  borderRadius: '8px',
                  border: '1px solid #fbbf24'
                }}>
                  <p style={{ margin: 0, color: '#92400e' }}>
                    No se encontraron rutas para esta transacción.
                  </p>
                </div>
              ) : (
                <div className="table-responsive" style={{ 
                  borderRadius: '12px', 
                  overflow: 'hidden', 
                  border: '2px solid #e5e7eb',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                }}>
                  <table className="table" style={{ marginTop: '0', width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                    <thead>
                      <tr style={{ backgroundColor: '#1e40af', color: 'white' }}>
                        <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Número de Ruta</th>
                        <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Destino</th>
                        <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Estado</th>
                        <th style={{ padding: '14px 16px', textAlign: 'right', fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Precio Transporte</th>
                        <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Fecha de Creación</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailRoutes.map((route, index) => (
                        <tr 
                          key={route._id}
                          style={{
                            backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                            borderBottom: '1px solid #e5e7eb',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#eff6ff'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9fafb'}
                        >
                          <td data-label="Número de Ruta" style={{ padding: '16px', fontWeight: '600' }}>
                            <span style={{ 
                              color: '#1e40af', 
                              fontSize: '14px',
                              backgroundColor: '#eff6ff',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              display: 'inline-block'
                            }}>
                              {route.routeNumber}
                            </span>
                          </td>
                          <td data-label="Destino" style={{ padding: '16px', color: '#374151', fontSize: '14px' }}>{route.endPoint || 'N/A'}</td>
                          <td data-label="Estado" style={{ padding: '16px', textAlign: 'center' }}>
                            <span className={`badge badge-${route.status === 'completed' ? 'completed' : route.status === 'rejected' ? 'rejected' : 'pending'}`} style={{ 
                              textTransform: 'none',
                              padding: '6px 14px',
                              borderRadius: '8px',
                              fontSize: '12px',
                              fontWeight: '600',
                              display: 'inline-block'
                            }}>
                              {route.status === 'completed' ? 'Completada' : route.status === 'rejected' ? 'Rechazada' : route.status}
                            </span>
                          </td>
                          <td data-label="Precio Transporte" style={{ padding: '16px', textAlign: 'right', color: '#374151', fontWeight: '600', fontSize: '14px' }}>
                            {formatCurrency(route.price || 0)}
                          </td>
                          <td data-label="Fecha de Creación" style={{ padding: '16px', color: '#6b7280', fontSize: '13px' }}>
                            {formatDate(route.createdAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Payment Photo */}
            {detailTransaction.paymentPhoto && detailTransaction.paymentPhoto.path && (
              <div style={{ 
                marginTop: '40px', 
                paddingTop: '28px', 
                borderTop: '3px solid #e5e7eb' 
              }}>
                <h3 style={{ 
                  marginBottom: '24px', 
                  color: '#1e40af',
                  fontSize: '22px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <span style={{ fontSize: '24px' }}>📷</span> Foto del Pago
                </h3>
                <div style={{ 
                  textAlign: 'center',
                  backgroundColor: '#ffffff',
                  padding: '24px',
                  borderRadius: '16px',
                  border: '2px solid #e5e7eb',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                }}>
                  <img
                    src={detailTransaction.paymentPhoto.path}
                    alt="Foto del pago"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '400px',
                      height: 'auto',
                      borderRadius: '12px',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      border: '2px solid #e5e7eb'
                    }}
                    onClick={() => {
                      setShowDetailModal(false);
                      handlePhotoClick(detailTransaction);
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'scale(1.02)';
                      e.target.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1)';
                      e.target.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
                    }}
                    onError={(e) => {
                      console.error('Error loading payment photo:', detailTransaction.paymentPhoto.path);
                      const errorMsg = document.createElement('p');
                      errorMsg.style.color = '#ef4444';
                      errorMsg.textContent = 'Error al cargar la imagen.';
                      e.target.parentElement.appendChild(errorMsg);
                      e.target.style.display = 'none';
                    }}
                  />
                  <p style={{ 
                    marginTop: '16px', 
                    color: '#6b7280', 
                    fontSize: '14px',
                    fontStyle: 'italic'
                  }}>
                    👆 Click en la imagen para ver en tamaño completo
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && sharedPayment && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '40px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            position: 'relative',
            border: '1px solid #e5e7eb'
          }}>
            <button
              onClick={() => {
                setShowApprovalModal(false);
                setFacturaFile(null);
              }}
              disabled={uploadingFactura}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                cursor: uploadingFactura ? 'not-allowed' : 'pointer',
                fontSize: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                opacity: uploadingFactura ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (!uploadingFactura) {
                  e.target.style.background = '#dc2626';
                  e.target.style.transform = 'scale(1.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (!uploadingFactura) {
                  e.target.style.background = '#ef4444';
                  e.target.style.transform = 'scale(1)';
                }
              }}
            >
              ×
            </button>
            
            <h3 style={{ 
              marginBottom: '24px', 
              color: '#10b981',
              fontSize: '24px',
              fontWeight: '600',
              paddingRight: '40px'
            }}>
              ✓ Aprobar Pago y Subir Factura
            </h3>
            
            <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f0fdf4', borderRadius: '8px' }}>
              <div style={{ fontSize: '14px', color: '#166534', marginBottom: '8px', fontWeight: '600' }}>
                Resumen del Pago:
              </div>
              <div style={{ fontSize: '16px', color: '#166534', fontWeight: '700' }}>
                A Pagar: {formatCurrency(sharedPayment.toPay || 0)}
              </div>
            </div>
            
            <form onSubmit={handleApprovePayment}>
              <div className="form-group" style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '10px', 
                  fontWeight: '600', 
                  color: '#374151',
                  fontSize: '14px'
                }}>
                  Factura (Bill) <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <div style={{
                  position: 'relative',
                  border: '2px dashed #d1d5db',
                  borderRadius: '12px',
                  padding: '24px',
                  textAlign: 'center',
                  backgroundColor: '#f9fafb',
                  transition: 'all 0.3s ease',
                  cursor: uploadingFactura ? 'not-allowed' : 'pointer',
                  opacity: uploadingFactura ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!uploadingFactura) {
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.backgroundColor = '#eff6ff';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!uploadingFactura) {
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                  }
                }}
                onClick={() => {
                  if (!uploadingFactura) {
                    const fileInput = document.getElementById('facturaInput');
                    if (fileInput) fileInput.click();
                  }
                }}
                >
                  <input
                    id="facturaInput"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setFacturaFile(e.target.files[0])}
                    required
                    disabled={uploadingFactura}
                    style={{ 
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      top: 0,
                      left: 0,
                      opacity: 0,
                      cursor: uploadingFactura ? 'not-allowed' : 'pointer'
                    }}
                  />
                  {!facturaFile ? (
                    <>
                      <div style={{ fontSize: '48px', marginBottom: '12px' }}>📄</div>
                      <p style={{ 
                        margin: '0 0 8px 0', 
                        color: '#374151', 
                        fontWeight: '600',
                        fontSize: '16px'
                      }}>
                        Click para seleccionar la factura
                      </p>
                      <p style={{ 
                        margin: 0, 
                        color: '#6b7280', 
                        fontSize: '14px'
                      }}>
                        o arrastra y suelta aquí
                      </p>
                      <p style={{ 
                        margin: '8px 0 0 0', 
                        color: '#9ca3af', 
                        fontSize: '12px'
                      }}>
                        PDF, PNG, JPG hasta 10MB
                      </p>
                    </>
                  ) : (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div style={{
                        fontSize: '48px',
                        color: '#10b981'
                      }}>✓</div>
                      <div style={{
                        backgroundColor: '#d1fae5',
                        color: '#059669',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        fontWeight: '600',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span>📄</span>
                        <span>{facturaFile.name}</span>
                      </div>
                      <p style={{ 
                        margin: 0, 
                        color: '#6b7280', 
                        fontSize: '13px',
                        fontStyle: 'italic'
                      }}>
                        Click para cambiar el archivo
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '28px' }}>
                <button 
                  type="button"
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowApprovalModal(false);
                    setFacturaFile(null);
                  }}
                  disabled={uploadingFactura}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    fontWeight: '500',
                    transition: 'all 0.2s',
                    border: 'none',
                    cursor: uploadingFactura ? 'not-allowed' : 'pointer',
                    opacity: uploadingFactura ? 0.5 : 1
                  }}
                >
                  Cancelar
                </button>
                <button 
                  type="submit"
                  className="btn btn-success"
                  disabled={uploadingFactura || !facturaFile}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    transition: 'all 0.2s',
                    border: 'none',
                    cursor: (uploadingFactura || !facturaFile) ? 'not-allowed' : 'pointer',
                    opacity: (uploadingFactura || !facturaFile) ? 0.5 : 1
                  }}
                >
                  {uploadingFactura ? '⏳ Subiendo...' : '✓ Aprobar y Subir'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && sharedPayment && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '40px',
            maxWidth: '600px',
            width: '100%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            position: 'relative',
            border: '1px solid #e5e7eb'
          }}>
            <button
              onClick={() => {
                setShowRejectModal(false);
                setRejectionReason('');
              }}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                cursor: 'pointer',
                fontSize: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#dc2626';
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#ef4444';
                e.target.style.transform = 'scale(1)';
              }}
            >
              ×
            </button>
            
            <h3 style={{ 
              marginBottom: '24px', 
              color: '#ef4444',
              fontSize: '24px',
              fontWeight: '600',
              paddingRight: '40px'
            }}>
              ✕ Rechazar Pago
            </h3>
            
            <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca' }}>
              <div style={{ fontSize: '14px', color: '#991b1b', marginBottom: '8px', fontWeight: '600' }}>
                ⚠️ Advertencia:
              </div>
              <div style={{ fontSize: '14px', color: '#991b1b' }}>
                Estás a punto de rechazar este pago. El administrador podrá compartir un nuevo monto después del rechazo.
              </div>
            </div>
            
            <form onSubmit={handleRejectPayment}>
              <div className="form-group" style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '10px', 
                  fontWeight: '600', 
                  color: '#374151',
                  fontSize: '14px'
                }}>
                  Razón del Rechazo <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Por favor, explica por qué rechazas este pago..."
                  required
                  rows={5}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    transition: 'border-color 0.2s',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#ef4444';
                    e.target.style.outline = 'none';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                  }}
                />
              </div>
              
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectionReason('');
                  }}
                  disabled={rejecting}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    fontWeight: '500',
                    transition: 'all 0.2s',
                    border: '1px solid #d1d5db',
                    backgroundColor: 'white',
                    color: '#374151',
                    cursor: rejecting ? 'not-allowed' : 'pointer',
                    opacity: rejecting ? 0.5 : 1
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={rejecting || !rejectionReason.trim()}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    transition: 'all 0.2s',
                    border: 'none',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    cursor: (rejecting || !rejectionReason.trim()) ? 'not-allowed' : 'pointer',
                    opacity: (rejecting || !rejectionReason.trim()) ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!rejecting && rejectionReason.trim()) {
                      e.target.style.backgroundColor = '#dc2626';
                      e.target.style.transform = 'translateY(-1px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#ef4444';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  {rejecting ? '⏳ Rechazando...' : '✕ Rechazar Pago'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserPayments;
